<template>
  <CompaniesAnalysisLayout
    :not-available-type="modifiedNotAvailableType"
    :company-uuid="companyUuid"
    :is-more-than-editor="showMoreThanEditorAuthority"
  >
    <template #header>
      <CompaniesAnalysisQscasHeader
        @on-date-range-changed="handleChangeDateRange"
      />
    </template>
    <template #graph>
      <Loading v-show="loading" />
      <div v-if="!loading" class="chartArea">
        <div class="chartArea__header">
          <div class="analysisTarget">
            {{ $t('pages.companies.analysis.qscas.analysisTarget') }}: <span class="u-fwB">{{ qsca.fields.map(v => v.name).join('/') }}</span>
          </div>
          <div v-if="providedFeaturesCompetitor">
            <div class="c-radio__item">
              <input
                :id="'competitor_radio_btn'"
                :checked="competitorMode"
                class="c-radio__input"
                type="checkbox"
                :disabled="loading"
                @change="handleChangeCompetitorMode"
              >
              <label class="c-radio__label" :for="'competitor_radio_btn'">{{ $t('pages.companies.analysis.reviews.displayCompetitorData') }}</label>
            </div>
          </div>
        </div>
        <div class="chartArea__main">
          <div class="chart">
            <ChartRadar
              :chart-data="chartData(qsca)"
              :options="chartOptions"
            />
          </div>
          <div v-if="competitorMode" class="competitorCharts">
            <div class="competitorCharts__header">
              {{ $t('pages.companies.analysis.qscas.competitorCharts.header', { count: qsca.competitorData.length }) }}
            </div>
            <div class="competitorCharts__charts">
              <template v-for="(store,storeKey) in qscas">
                <div v-for="(data,key) in store.competitorData"
                     :key="'competitorQsca_' + key + 'store' + storeKey"
                     class="competitorChart"
                >
                  <ChartRadar
                    :chart-data="competitorChartData(store,data)"
                    :options="chartOptions"
                  />
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </template>
    <template #table>
      <CompaniesAnalysisTable
        :display-checkbox="false"
        :loading="loading"
        :items="qscas"
        :static-heads="dataTypes"
        :cross-search-fields="crossSearchFields"
        :competitor-mode="competitorMode"
      />
    </template>
    <template #reviews>
      <Loading v-show="loading" />
      <div v-if="!loading" class="p-modalLikeBox" :class="{'p-modalLikeBox--competitor': showCompetitorReviews}">
        <div class="p-modalLikeBox__header">
          <button class="p-modalLikeBox__header__text" @click="switchCompetitorReviews">
            {{ $t('pages.companies.analysis.qscas.storeReviews') }}
          </button>
          <button v-if="competitorMode" class="competitorReviewTitle" @click="switchCompetitorReviews">
            {{ $t('pages.companies.analysis.qscas.competitorReviews') }}
          </button>
        </div>
        <div class="p-modalLikeBox__body">
          <div class="p-table__content">
            <div v-if="providedAiReviewSentencesAnalysis" class="p-box__info">
              <div class="c-info">
                <div class="c-info__icon">
                  <Icon name="iconQuestion" />
                </div>
                <div class="c-info__content">
                  <p>{{ $t('pages.companies.analysis.qscas.aiReviewDescription') }}</p>
                </div>
              </div>
            </div>
            <table>
              <thead>
                <tr>
                  <th v-if="!providedAiReviewSentencesAnalysis">
                    {{ $t('pages.companies.analysis.qscas.reviewsTable.keyword') }}
                  </th>
                  <th>
                    {{ $t('pages.companies.analysis.qscas.reviewsTable.store') }}
                  </th>
                  <th>
                    {{ $t('pages.companies.analysis.qscas.reviewsTable.rate') }}
                  </th>
                  <th>
                    {{ $t('pages.companies.analysis.qscas.reviewsTable.detail') }}
                  </th>
                </tr>
                <tr class="p-table__content__space"><td colspan="4" /></tr>
              </thead>
              <tbody>
                <tr v-if="displayReviews.length === 0">
                  <td colspan="4">{{ $t('messages.noData') }}</td>
                </tr>
                <tr v-for="(review,i) in displayReviews" v-else :key="'review_' + i">
                  <td v-if="!providedAiReviewSentencesAnalysis">
                    {{ review.keywords.join(',') }}
                  </td>
                  <td class="reviewStoreName">
                    {{ review.storeName }}
                  </td>
                  <td>
                    <div class="p-reviews__summary">
                      <div class="p-reviews__site">
                        <div class="p-reviews__site__icon">
                          <KkSiteIcon :name="review.siteType" />
                        </div>
                        <div
                          class="p-reviews__site__name"
                          data-test="review_item_site_name"
                          v-text="review.siteName"
                        />
                      </div>
                      <div class="p-reviews__reviewRating">
                        <div class="c-rating">
                          <div class="c-rating__number">
                            <template v-if="review.rating">
                              {{ review.rating }}
                            </template>
                            <template v-else>
                              -
                            </template>
                          </div>
                          <Star
                            v-show="review.rating"
                            :rating="review.rating"
                          />
                        </div>
                      </div>
                      <div
                        class="p-reviews__postedDate"
                        v-text="formattedDateString(review.postedAt)"
                      />
                      <div
                        class="p-reviews__reviewer"
                        v-text="review.reviewerName"
                      />
                    </div>
                  </td>
                  <td v-if="!providedAiReviewSentencesAnalysis" v-html="comment(sanitize(review.comment), review.keywords)" />
                  <td v-if="providedAiReviewSentencesAnalysis">
                    <div v-if="review.reviewSentences.length == 0">
                      <p class="noAIReview">{{ $t('pages.companies.analysis.qscas.noAiReview') }}</p>
                      <p v-html="comment(sanitize(review.comment), review.keywords)" />
                    </div>
                    <p v-for="(sentence, j) in review.reviewSentences" :key="'sentence_' + j">
                      {{ sentence.category === "UNCLASSIFIED" ? "" : `[${sentence.category[0]}]` }}
                      <span :class="[ sentence.category === 'UNCLASSIFIED' ? '' : 'qscaReview', sentence.sentiment.toLowerCase() + 'Review' ]">{{ sentence.sentence }}</span>
                    </p>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </template>
  </CompaniesAnalysisLayout>
</template>

<script>
import DOMPurify from 'dompurify'
import { mapGetters, mapState, mapActions } from 'vuex'
import moment from 'moment'
import CompaniesAnalysisLayout from '../../../../layouts/CompaniesAnalysisLayout'
import CompaniesAnalysisQscasHeader from '../../../../components/CompaniesAnalysis/Qscas/Header'
import ChartRadar from '../../../../components/ChartRadar'
import CompaniesAnalysisTable from '../../../../components/CompaniesAnalysis/Table'
import Star from '../../../../components/Star'
import Loading from '../../../../components/Loading'
import { KkSiteIcon } from '../../../../components/base'
import { syntaxHighlight } from '../../../../modules/syntaxHighlight'
import companiesNotAvailableTypeMixin from '../../../../mixins/companiesNotAvailableTypeMixin'
import Icon from 'src/biz_spa/components/Icon.vue'

export default {
  name: 'CompaniesAnalysisQscasDetail',
  components: {
    Icon,
    Loading,
    CompaniesAnalysisLayout,
    CompaniesAnalysisQscasHeader,
    ChartRadar,
    CompaniesAnalysisTable,
    Star,
    KkSiteIcon
  },
  mixins: [companiesNotAvailableTypeMixin],
  data() {
    return {
      showCompetitorReviews: false,
    }
  },
  computed: {
    ...mapGetters({
      providedFeaturesCompetitor: 'companiesCompany/providedFeaturesCompetitor',
      dataTypes: 'companiesAnalysisQscas/dataTypes',
      crossSearchFields: 'companiesAnalysisQscas/crossSearchFields',
      startTime: 'companiesAnalysisQscas/startTime',
      endTime: 'companiesAnalysisQscas/endTime',
      showMoreThanEditorAuthority: 'companiesAccessibleApi/showMoreThanEditorAuthority',
      providedAiReviewSentencesAnalysis: 'companiesCompany/providedAiReviewSentencesAnalysis',
    }),
    ...mapState({
      competitorMode: state => state.companiesAnalysisQsca.competitorMode,
      qscas: state => state.companiesAnalysisQsca.qscas,
      loading: state => state.companiesAnalysisQsca.loading,
      storeReviews: state => state.companiesAnalysisQsca.storeReviews,
      competitorReviews: state => state.companiesAnalysisQsca.competitorReviews,
    }),
    modifiedNotAvailableType() {
      if (!this.competitorMode && this.notAvailableType === 'emptyCompetitor') return null
      return this.notAvailableType
    },
    // 「全て」のデータの詳細画面だった場合
    allOnly() {
      return this.qscas.length === 1
    },
    allQsca() {
      return this.qscas[0]
    },
    qsca() {
      return this.allOnly ? this.qscas[0] : this.qscas[1]
    },
    query() {
      return {
        start_time: this.startTime,
        end_time: this.endTime,
        display_competitor: this.competitorMode,
        analysis_axis: this.$route.query.analysis_axis,
        analysis_axis_id: this.$route.query.analysis_axis_id,
        cross_analysis_axis: this.$route.query.cross_analysis_axis,
        cross_analysis_axis_id: this.$route.query.cross_analysis_axis_id
      }
    },
    companyUuid() {
      return this.$route.params.company_uuid
    },
    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: true,
        scales: {
          r: {
            suggestedMin: 0,
            suggestedMax: 100,
            ticks: {
              stepSize: 20
            },
          },
        },
      }
    },
    displayReviews() {
      return this.showCompetitorReviews ? this.competitorReviews : this.storeReviews
    }
  },
  created() {
    this.changeCompetitorMode(false)
    this.changeProvidedAiReviewSentencesAnalysis(this.providedAiReviewSentencesAnalysis)
    this.readAnalysisQsca(this.query)
  },
  methods: {
    ...mapActions({
      readAnalysisQsca: 'companiesAnalysisQsca/readAnalysisQsca',
      changeCompetitorMode: 'companiesAnalysisQsca/changeCompetitorMode',
      changeProvidedAiReviewSentencesAnalysis: 'companiesAnalysisQsca/changeProvidedAiReviewSentencesAnalysis',
      setSelectedYear: 'companiesAnalysisQscas/setSelectedYear',
      setSelectedMonth: 'companiesAnalysisQscas/setSelectedMonth',
      setSelectedPeriod: 'companiesAnalysisQscas/setSelectedPeriod',
    }),
    comment(text, keywords) {
      return syntaxHighlight(text, keywords)
    },
    sanitize(html) {
      return DOMPurify.sanitize(html)
    },
    switchCompetitorReviews() {
      if (!this.competitorMode) return
      this.showCompetitorReviews = !this.showCompetitorReviews
    },
    handleChangeCompetitorMode() {
      this.changeCompetitorMode(!this.competitorMode)
      this.readAnalysisQsca(this.query)
    },
    handleChangeDateRange({value, type}) {
      switch (type) {
      case 'period':
        this.setSelectedPeriod(value)
        break
      case 'month':
        this.setSelectedMonth(value)
        break
      case 'year':
        this.setSelectedYear(value)
        break
      }
      this.readAnalysisQsca(this.query)
    },
    competitorChartData(data, competitor) {
      return {
        labels: this.dataTypes.map(v => v.name),
        datasets: [
          {
            label: data.fields.map(v => v.name).join('/'),
            data: this.dataTypes.map(v => data[v.id]),
            borderColor: '#E9446A',
            backgroundColor: 'transparent'
          },
          {
            label: competitor.fields.map(v => v.name).join('/'),
            data: this.dataTypes.map(v => competitor[v.id]),
            borderColor: '#46555F',
            backgroundColor: 'transparent'
          },
        ]
      }
    },
    chartData(data) {
      const obj = {
        labels: this.dataTypes.map(v => v.name),
        datasets: [
          {
            label: data.fields.map(v => v.name).join('/'),
            data: this.dataTypes.map(v => data[v.id]),
            borderColor: '#E9446A',
            backgroundColor: 'transparent'
          },
        ]
      }
      if (!this.allOnly) {
        obj.datasets.push({
          label: this.allQsca.fields.map(v => v.name).join('/'),
          data: this.dataTypes.map(v => this.allQsca[v.id]),
          borderColor: '#bbbbbb',
          backgroundColor: 'transparent'
        })
      }
      return obj
    },
    formattedDateString(date) {
      return moment(date).format(this.$t('format.datetime.default'))
    },
  }
}
</script>

<style lang="scss" scoped>
@import '../../../../../../stylesheets/biz_spa/foundation/_var.scss';

.chart {
  width: 30%;
  margin: 0 42px;
}
.p-modalLikeBox {
  margin-top: 48px;
  &__header {
    display: flex;
  }
  &__header__text {
    cursor: default;
  }
  &__body {
    border-top: 1px solid #E4E7EB;
    padding: 32px;
  }
  &--competitor {
    background: $color-competitor-bg;
    .competitorReviewTitle {
      color: $color-font-reverse;
      font-weight: bold;
      cursor: default;
    }
    .p-modalLikeBox__header__text {
      color: $color-font-gloomy;
      font-weight: normal;
      cursor: pointer;
    }
  }
}
:deep(.chartArea__header) {
  display: flex;
}
:deep(.chartArea__main) {
  display: flex;
}
.competitorCharts {
  border: 1px solid #E4E7EB;
  border-radius: 4px;
  width: 70%;
  padding: 24px 32px;
  &__header {
    font-size: 18px;
    font-weight: bold;
    display: flex;
  }
  &__charts {
    display: flex;
    flex-shrink: 0;
    overflow: auto;
  }
}
.competitorChart {
  min-width: 35%;
  width: 35%;
  margin-right: 42px;
}
.competitorReviewTitle {
  color: $color-accent-primary;
  padding: 16px 0;
}
:deep(.highlight) {
  color: $color-accent-primary;
}
.reviewStoreName {
  width: 15%;
}
.noAIReview {
  font-weight: bold;
}
.qscaReview {
  font-weight: bold;
}
.positiveReview {
  color: $complete-color;
}
.negativeReview {
  color: $color-accent-primary;
}
</style>
